import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Ticket, Plus, Trash2, Edit3, Filter, CheckCircle2, Clock, Circle, XCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import useFocusItem from '@/hooks/useFocusItem';

const PRIORITY = {
  critical: { label: 'Critique', color: 'border-rose-500/40 bg-rose-500/5 text-rose-300', dot: 'bg-rose-500', order: 0 },
  high:     { label: 'Haute',    color: 'border-amber-500/40 bg-amber-500/5 text-amber-300', dot: 'bg-amber-500', order: 1 },
  medium:   { label: 'Moyenne',  color: 'border-sky-500/40 bg-sky-500/5 text-sky-300', dot: 'bg-sky-500', order: 2 },
  low:      { label: 'Faible',   color: 'border-zinc-700 bg-zinc-800/50 text-zinc-400', dot: 'bg-zinc-500', order: 3 },
};
const STATUS = {
  open:        { label: 'Ouvert',     icon: Circle,        color: 'text-sky-400' },
  in_progress: { label: 'En cours',   icon: Clock,         color: 'text-amber-400' },
  resolved:    { label: 'Résolu',     icon: CheckCircle2,  color: 'text-emerald-400' },
  closed:      { label: 'Fermé',      icon: XCircle,       color: 'text-zinc-500' },
};
const TYPES = {
  bug:         { label: 'Bug',          color: 'bg-rose-500/10 text-rose-300' },
  feature:     { label: 'Fonctionnalité', color: 'bg-violet-500/10 text-violet-300' },
  improvement: { label: 'Amélioration', color: 'bg-emerald-500/10 text-emerald-300' },
  question:    { label: 'Question',     color: 'bg-sky-500/10 text-sky-300' },
};

export default function TicketsAdminPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', priority: 'all', type: 'all' });
  const [editing, setEditing] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  useFocusItem(tickets);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (filters.type !== 'all') params.type = filters.type;
      const { data } = await api.get('/tickets', { params });
      setTickets(data);
    } catch (e) {
      toast.error('Erreur chargement tickets');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce ticket ?')) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success('Ticket supprimé');
      fetchTickets();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Tickets & Roadmap
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tickets.length} ticket(s) — feedback des sous-traitants pour les prochaines mises à jour
          </p>
        </div>
        <Button data-testid="new-ticket-btn" onClick={() => setNewOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nouveau ticket
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-zinc-500" />
        <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-200 rounded-sm w-44 h-9 text-sm" data-testid="filter-status">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters(f => ({ ...f, priority: v }))}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-200 rounded-sm w-44 h-9 text-sm" data-testid="filter-priority">
            <SelectValue placeholder="Priorité" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
            <SelectItem value="all">Toutes priorités</SelectItem>
            {Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-200 rounded-sm w-44 h-9 text-sm" data-testid="filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
            <SelectItem value="all">Tous types</SelectItem>
            {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Chargement…</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">Aucun ticket pour le moment</div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => {
            const p = PRIORITY[t.priority] || PRIORITY.medium;
            const s = STATUS[t.status] || STATUS.open;
            const ty = TYPES[t.type] || TYPES.feature;
            const StatusIcon = s.icon;
            return (
              <Card key={t.id} data-focus-id={t.id} className={`bg-zinc-900 border ${p.color.split(' ')[0]} rounded-sm`} data-testid={`ticket-card-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full ${p.dot} mt-1.5 shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-zinc-100">{t.title}</h3>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${ty.color}`}>{ty.label}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${p.color}`}>{p.label}</span>
                          <span className={`inline-flex items-center gap-1 text-[10px] ${s.color}`}>
                            <StatusIcon className="w-3 h-3" /> {s.label}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-zinc-400 whitespace-pre-line line-clamp-3 mb-2">{t.description}</p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] text-zinc-600">
                          {t.created_by_company || t.created_by_name} · {t.created_by_role} · {new Date(t.created_at).toLocaleDateString('fr-FR')}
                          {t.lang === 'en' && <span className="ml-1.5 px-1 bg-zinc-800 rounded-sm text-zinc-400">EN</span>}
                        </p>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditing(t)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm h-7 text-xs px-2" data-testid={`edit-ticket-${t.id}`}>
                            <Edit3 className="w-3 h-3 mr-1" /> Éditer
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(t.id)} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-sm h-7 text-xs px-2" data-testid={`delete-ticket-${t.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      {t.admin_response && (
                        <div className="mt-2 bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-2 text-xs text-emerald-200">
                          <MessageSquare className="w-3 h-3 inline mr-1" /><strong>Réponse :</strong> {t.admin_response}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New ticket dialog (admin can also create internal tickets) */}
      {newOpen && <TicketFormDialog open={newOpen} onOpenChange={setNewOpen} onSaved={fetchTickets} />}
      {editing && <TicketFormDialog open={!!editing} onOpenChange={() => setEditing(null)} ticket={editing} onSaved={fetchTickets} isAdmin />}
    </div>
  );
}

function TicketFormDialog({ open, onOpenChange, ticket, onSaved, isAdmin }) {
  const [form, setForm] = useState({
    title: ticket?.title || '',
    description: ticket?.description || '',
    type: ticket?.type || 'feature',
    priority: ticket?.priority || 'medium',
    status: ticket?.status || 'open',
    admin_response: ticket?.admin_response || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Titre et description requis');
      return;
    }
    setSaving(true);
    try {
      if (ticket) {
        await api.put(`/tickets/${ticket.id}`, form);
        toast.success('Ticket mis à jour');
      } else {
        await api.post('/tickets', { ...form, lang: 'fr' });
        toast.success('Ticket créé');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="ticket-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <Ticket className="w-5 h-5 text-amber-400" />
            {ticket ? 'Modifier le ticket' : 'Nouveau ticket'}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {ticket ? `Soumis par ${ticket.created_by_company || ticket.created_by_name}` : "Décrivez ce qui doit être amélioré ou corrigé."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Titre</Label>
            <Input data-testid="ticket-title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Description</Label>
            <Textarea data-testid="ticket-description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={5} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="ticket-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Priorité</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label className="text-xs text-zinc-400">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="ticket-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          {isAdmin && (
            <div>
              <Label className="text-xs text-zinc-400">Réponse admin <span className="text-zinc-600">(notifié au soumetteur sur résolution)</span></Label>
              <Textarea data-testid="ticket-admin-response" value={form.admin_response} onChange={(e) => setForm(f => ({ ...f, admin_response: e.target.value }))} rows={3} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" placeholder="Commentaire qui sera envoyé au sous-traitant…" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium" data-testid="save-ticket-btn">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
